import React, { useEffect, useState, useRef } from 'react';
import { Camera, SendHorizontal, X, Image as ImageIcon, FileText, Download } from 'lucide-react';
import chauffeurService from '../../services/chauffeurService';
import { getConversation, sendMessage, sendMessageWithImage } from '../../services/messageService';
import { useUser } from '../../context/UserContext';
import DashboardLayout from '../../components/layout/DashboardLayout';
import './MessagerieInterne.css';
import { useI18n } from '../../i18n/I18nContext';
import realtime from '../../services/realtime';

const getInitials = (firstname, lastname) => {
  return `${firstname?.charAt(0) || ''}${lastname?.charAt(0) || ''}`.toUpperCase();
};

const formatDate = (dateStr, locale = 'fr-FR') => {
  const date = new Date(dateStr);
  return date.toLocaleDateString(locale === 'en' ? 'en-US' : 'fr-FR', { year: 'numeric', month: '2-digit', day: '2-digit' });
};

const MessagerieInterne = () => {
  const { userId } = useUser();
  const { t } = useI18n();
  const [chauffeurs, setChauffeurs] = useState([]);
  const [selectedChauffeur, setSelectedChauffeur] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [sending, setSending] = useState(false);
  const chatBodyRef = useRef(null);
  const subscribedRef = useRef(false);
  const [unreadCounts, setUnreadCounts] = useState({}); // { [chauffeurId]: number }
  const [banner, setBanner] = useState(null); // { text, ts }
  const selectedChauffeurRef = useRef(selectedChauffeur);

  useEffect(() => {
    selectedChauffeurRef.current = selectedChauffeur;
  }, [selectedChauffeur]);

  const [selectedImage, setSelectedImage] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const fileInputRef = useRef(null);
  const apiBase = process.env.REACT_APP_API_URL
    ? process.env.REACT_APP_API_URL.replace(/\/$/, '')
    : (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8080');

  useEffect(() => {
    const fetchChauffeurs = async () => {
      try {
        const data = await chauffeurService.getAllDrivers();
        setChauffeurs(data);
      } catch (err) {
        setError(t('messagerie.errors.fetchChauffeurs'));
      } finally {
        setLoading(false);
      }
    };
    fetchChauffeurs();
  }, []);

  useEffect(() => {
    if (selectedChauffeur && userId) {
      setMessages([]);
      setError(null);
      setLoading(true);
      getConversation(userId, selectedChauffeur.id)
        .then(setMessages)
        .catch(() => setError(t('messagerie.errors.fetchConversation')))
        .finally(() => setLoading(false));
    }
  }, [selectedChauffeur, userId]);

  useEffect(() => {
    if (chatBodyRef.current) {
      chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (!userId || subscribedRef.current) return;
    subscribedRef.current = true;
    const dest = `/topic/messages.${userId}`;
    realtime.subscribe(dest, (msg) => {
      // Use Ref to get the LATEST selected chauffeur even in this stale closure
      const currentSelected = selectedChauffeurRef.current;
      if (currentSelected && String(msg.senderId) === String(currentSelected.id)) {
        setMessages((prev) => [...prev, msg]);
        // Reset unread for the active chat
        setUnreadCounts((prev) => ({ ...prev, [currentSelected.id]: 0 }));
      }
      // Else, we could show a toast/badge; skipped for brevity
      else {
        // Increment unread badge for that chauffeur
        setUnreadCounts((prev) => ({ ...prev, [msg.senderId]: (prev[msg.senderId] || 0) + 1 }));
        // Show a lightweight banner
        const sender = chauffeurs.find((c) => String(c.id) === String(msg.senderId));
        const name = sender ? `${sender.firstname} ${sender.lastname}` : t('messagerie.banner.unknownChauffeur', { id: msg.senderId });
        setBanner({ text: t('messagerie.banner.newMessage', { name }), ts: Date.now() });
        // Auto-hide after 3s
        setTimeout(() => {
          setBanner((b) => (b && Date.now() - b.ts >= 2800 ? null : b));
        }, 3000);
      }
    });
  }, [userId, selectedChauffeur]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if ((!messageInput.trim() && !selectedImage) || !selectedChauffeur) return;
    setSending(true);
    try {
      const newMsg = {
        senderId: userId,
        receiverId: selectedChauffeur.id,
        content: messageInput.trim() || 'Photo',
      };
      const saved = await sendMessageWithImage(newMsg, selectedImage);
      setMessages((prev) => [...prev, saved]);
      setMessageInput('');
      setSelectedImage(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) {
      setError(t('messagerie.errors.sendFailed'));
    } finally {
      setSending(false);
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);
    }
  };

  const handlePaste = (e) => {
    const items = e.clipboardData.items;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf('image') !== -1) {
        const file = items[i].getAsFile();
        if (file) {
          setSelectedImage(file);
          // Optional: prevent default if we only want to paste the image and not the text if both exist
          // e.preventDefault(); 
        }
        break;
      }
    }
  };

  // Group messages by date for date separators
  const renderMessagesWithDates = () => {
    let lastDate = null;
    return messages.map((msg, idx) => {
      const msgDate = formatDate(msg.sentAt, t('common.dashboard') === 'Dashboard' ? 'en' : 'fr');
      const showDate = msgDate !== lastDate;
      lastDate = msgDate;
      // Compare as strings for robustness
      const isSent = String(msg.senderId) === String(userId);
      return (
        <React.Fragment key={msg.id || `${idx}-${msg.sentAt}`}>
          {showDate && (
            <div className="messagerie-date-separator">{msgDate}</div>
          )}
          <div style={{ display: 'flex', alignItems: 'flex-end', marginBottom: '0.125rem', flexDirection: isSent ? 'row-reverse' : 'row' }}>
            {/* Only show avatar for received messages */}
            {!isSent && (
              <div className="messagerie-message-avatar">
                {selectedChauffeur?.firstname && selectedChauffeur?.lastname
                  ? getInitials(selectedChauffeur.firstname, selectedChauffeur.lastname)
                  : <span>?</span>}
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: isSent ? 'flex-end' : 'flex-start' }}>
              <div
                className={`messagerie-message-bubble${isSent ? ' sent' : ' received'}${msg.imageUrl ? ' has-image' : ''}`}
                style={isSent ? { marginLeft: 'auto' } : {}}
              >
                {msg.imageUrl && (
                  <div className="messagerie-message-image-container" onClick={() => window.open(`${apiBase}${msg.imageUrl}`, '_blank')}>
                    {msg.imageUrl.toLowerCase().endsWith('.pdf') ? (
                      <div style={{ padding: '1rem', display: 'flex', alignItems: 'center', gap: '0.75rem', background: '#f8fafc', minWidth: '200px' }}>
                        <FileText size={32} color="#ef4444" />
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>Document PDF</span>
                          <span style={{ fontSize: '0.7rem', color: '#64748b' }}>Cliquez pour ouvrir</span>
                        </div>
                      </div>
                    ) : (
                      <img
                        src={`${apiBase}${msg.imageUrl}`}
                        alt="Attachment"
                      />
                    )}
                  </div>
                )}
                {msg.content && msg.content !== 'Photo' && (
                  <div className={msg.imageUrl ? "messagerie-message-content-under-image" : "messagerie-message-content"}>
                    {msg.content}
                  </div>
                )}
              </div>
              <div className={`messagerie-message-timestamp${isSent ? ' sent' : ' received'}`}>
                {new Date(msg.sentAt).toLocaleTimeString(t('common.dashboard') === 'Dashboard' ? 'en-US' : 'fr-FR', { hour: '2-digit', minute: '2-digit' })}
              </div>
            </div>
          </div>
        </React.Fragment>
      );
    });
  };

  return (
    <DashboardLayout>
      <div className="messagerie-container">
        <div className="messagerie-sidebar">
          <h4 className="messagerie-title">{t('messagerie.title')}</h4>
          {loading && <div className="messagerie-loading">{t('common.loading')}</div>}
          {error && <div className="messagerie-error">{error}</div>}
          <ul className="messagerie-chauffeur-list">
            {chauffeurs.map((chauffeur) => (
              <li
                key={chauffeur.id}
                className={`messagerie-chauffeur-item${selectedChauffeur?.id === chauffeur.id ? ' selected' : ''}`}
                onClick={() => {
                  setSelectedChauffeur(chauffeur);
                  // Clear unread when opening
                  setUnreadCounts((prev) => ({ ...prev, [chauffeur.id]: 0 }));
                }}
              >
                <div className="messagerie-avatar">
                  {getInitials(chauffeur.firstname, chauffeur.lastname)}
                </div>
                <div className="messagerie-chauffeur-info">
                  <span className="messagerie-chauffeur-name">{chauffeur.firstname} {chauffeur.lastname}</span>
                  <span className="messagerie-chauffeur-email">{chauffeur.email}</span>
                </div>
                {!!unreadCounts[chauffeur.id] && (
                  <span style={{
                    marginLeft: 'auto',
                    background: '#ef4444',
                    color: 'white',
                    borderRadius: '9999rem',
                    padding: '0.125rem 0.5rem',
                    fontSize: '0.75rem',
                    fontWeight: 700,
                  }}>{unreadCounts[chauffeur.id]}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
        <div className="messagerie-chat-area">
          {banner && (
            <div style={{
              position: 'fixed', right: '1.25rem', bottom: '1.25rem', zIndex: 9999,
              background: 'rgba(15,23,42,0.95)', color: 'white',
              padding: '0.625rem 0.875rem', borderRadius: '0.625rem',
              boxShadow: '0 0.625rem 1.25rem rgba(0,0,0,0.15)'
            }}
              onClick={() => setBanner(null)}
            >{banner.text}</div>
          )}
          {selectedChauffeur ? (
            <div className="messagerie-chat-card">
              <div className="messagerie-chat-header">
                <div className="messagerie-avatar large">
                  {getInitials(selectedChauffeur.firstname, selectedChauffeur.lastname)}
                </div>
                <div>
                  <div className="messagerie-chat-name">{selectedChauffeur.firstname} {selectedChauffeur.lastname}</div>
                  <div className="messagerie-chat-email">{selectedChauffeur.email}</div>
                </div>
              </div>
              <div className="messagerie-chat-body" ref={chatBodyRef}>
                {loading ? (
                  <div className="messagerie-loading">{t('common.loading')}</div>
                ) : messages.length === 0 ? (
                  <p className="messagerie-placeholder">{t('messagerie.noMessages')}</p>
                ) : (
                  <div className="messagerie-messages-list">
                    {renderMessagesWithDates()}
                  </div>
                )}
              </div>
              <form className="messagerie-chat-input-row" onSubmit={handleSendMessage}>
                <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                  {selectedImage && (
                    <div style={{ padding: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <div style={{ position: 'relative' }}>
                        {selectedImage.type === 'application/pdf' ? (
                          <div style={{ height: '60px', width: '60px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fee2e2', borderRadius: '4px' }}>
                            <FileText color="#ef4444" size={30} />
                          </div>
                        ) : (
                          <img
                            src={URL.createObjectURL(selectedImage)}
                            alt="Selected"
                            style={{ height: '60px', width: '60px', objectFit: 'cover', borderRadius: '4px' }}
                          />
                        )}
                        <button
                          type="button"
                          onClick={() => setSelectedImage(null)}
                          style={{
                            position: 'absolute', top: '-8px', right: '-8px',
                            background: '#ef4444', color: 'white', border: 'none',
                            borderRadius: '50%', width: '22px', height: '22px',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            cursor: 'pointer', fontSize: '12px', boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                          }}
                        >
                          <X size={14} />
                        </button>
                      </div>
                      <span style={{ fontSize: '0.8rem', color: '#64748b' }}>{selectedImage.name}</span>
                    </div>
                  )}
                  <div style={{ display: 'flex', width: '100%', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="file"
                      accept="image/*,.pdf"
                      ref={fileInputRef}
                      onChange={handleImageSelect}
                      onClick={(e) => { e.target.value = null; }}
                      style={{ display: 'none' }}
                    />
                    <button
                      type="button"
                      className="messagerie-attachment-btn"
                      onClick={() => fileInputRef.current.click()}
                      style={{
                        background: '#f8fafc', border: '1px solid #e2e8f0',
                        borderRadius: '50%', width: '36px', height: '36px',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: 'pointer', color: '#64748b', transition: 'all 0.2s',
                        flexShrink: 0
                      }}
                      title="Ajouter une image"
                    >
                      <Camera size={20} />
                    </button>
                    <input
                      type="text"
                      className="messagerie-chat-input"
                      placeholder={t('messagerie.typeMessage')}
                      style={{ margin: 0 }}
                      value={messageInput}
                      onChange={e => setMessageInput(e.target.value)}
                      onPaste={handlePaste}
                      disabled={sending}
                      autoFocus
                    />
                    <button 
                      type="submit" 
                      className="messagerie-send-btn" 
                      disabled={sending || (!messageInput.trim() && !selectedImage)}
                      style={{ 
                        padding: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <SendHorizontal size={18} style={{ marginLeft: '2px' }} />
                    </button>
                  </div>
                </div>
              </form>
              {previewImage && (
                <div
                  style={{
                    position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
                    background: 'rgba(0,0,0,0.85)', zIndex: 10000,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    padding: '2rem'
                  }}
                  onClick={() => setPreviewImage(null)}
                >
                  <img
                    src={previewImage}
                    alt="Preview"
                    style={{ maxHeight: '100%', maxWidth: '100%', borderRadius: '4px' }}
                  />
                  <button
                    style={{
                      position: 'absolute', top: '1rem', right: '1rem',
                      background: 'rgba(255,255,255,0.1)', border: 'none', color: 'white',
                      borderRadius: '50%', width: '40px', height: '40px',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      cursor: 'pointer', transition: 'background 0.2s'
                    }}
                    onClick={() => setPreviewImage(null)}
                  >
                    <X size={24} />
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="messagerie-empty-chat">
              <p>{t('messagerie.placeholder')}</p>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default MessagerieInterne; 